import { getErrorMessage } from "@arti/utilities";
import { focusManager, QueryClient } from "@tanstack/react-query";

import type { ListChannelsAsUserResponse } from "../../dashboard-client/types.gen";
import type { DashboardRealtimeMessage } from "../../realtime/dashboardRealtimeTypes";

import { listChannelsAsUserOptions } from "../../dashboard-client/@tanstack/react-query.gen";
import { listChannelsAsUser } from "../../dashboard-client/sdk.gen";

export interface OrgChannelsScope {
  organizationId: string;
  userId: string;
  includeDeleted: boolean;
}

export function createOrgChannelsQueryStore(scope: OrgChannelsScope | null) {
  const client = new QueryClient({
    defaultOptions: { queries: { retry: false, staleTime: 30_000 } },
  });
  const query = {
    include_deleted: scope?.includeDeleted
      ? ("true" as const)
      : ("false" as const),
    include_dormant: "true" as const,
  };
  const generatedOptions = listChannelsAsUserOptions({ query });
  const pendingChannelIds = new Set<string>();
  let pendingFullRefresh = false;
  let needsFullFetch = true;
  let connected = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const options = {
    ...generatedOptions,
    enabled: scope !== null,
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const previous = client.getQueryData(generatedOptions.queryKey);
      const channelIds =
        needsFullFetch ||
        pendingFullRefresh ||
        !previous ||
        pendingChannelIds.size === 0 ||
        pendingChannelIds.size > 100
          ? []
          : [...pendingChannelIds];
      pendingChannelIds.clear();
      pendingFullRefresh = false;
      try {
        const { data, response } = await listChannelsAsUser({
          query: {
            ...query,
            ...(channelIds.length > 0
              ? { channel_ids: channelIds.join(",") }
              : {}),
          },
          signal,
          throwOnError: false,
        });
        if (!response.ok || !data) {
          if (
            connected &&
            (response.status === 401 || response.status === 403)
          ) {
            client.setQueryData(generatedOptions.queryKey, { channels: [] });
          }
          throw new Error(`HTTP ${response.status}`);
        }
        needsFullFetch = false;
        return previous && channelIds.length > 0
          ? mergeChannelRefresh(previous, data, channelIds)
          : data;
      } catch (error) {
        needsFullFetch = true;
        throw new Error(getErrorMessage(error), { cause: error });
      }
    },
  };

  function hasPendingRefresh() {
    return pendingFullRefresh || pendingChannelIds.size > 0;
  }

  function scheduleRefresh() {
    if (timer !== null || !hasPendingRefresh()) {
      return;
    }
    timer = setTimeout(() => {
      timer = null;
      const cached = client
        .getQueryCache()
        .find({ exact: true, queryKey: options.queryKey });
      if (
        !cached ||
        cached.getObserversCount() === 0 ||
        cached.state.fetchStatus !== "idle"
      ) {
        return;
      }
      void client.refetchQueries(
        { exact: true, queryKey: options.queryKey, type: "active" },
        { cancelRefetch: false },
      );
    }, 150);
  }

  function invalidate(channelId?: string) {
    if (!scope) {
      return;
    }
    if (channelId) {
      pendingChannelIds.add(channelId);
    } else {
      pendingFullRefresh = true;
    }
    void client.invalidateQueries({
      exact: true,
      queryKey: options.queryKey,
      refetchType: "none",
    });
    scheduleRefresh();
  }

  return {
    client,
    connect() {
      let disconnected = false;
      connected = true;
      client.mount();
      const reconciliationTimer = setInterval(() => {
        const cached = client
          .getQueryCache()
          .find({ exact: true, queryKey: options.queryKey });
        if (focusManager.isFocused() && cached?.isActive()) {
          invalidate();
        }
      }, 60_000);
      const unsubscribe = client.getQueryCache().subscribe((event) => {
        if (event.query.state.fetchStatus === "idle") {
          scheduleRefresh();
        }
      });
      return () => {
        if (disconnected) {
          return;
        }
        disconnected = true;
        unsubscribe();
        connected = false;
        clearInterval(reconciliationTimer);
        client.unmount();
        if (timer !== null) {
          clearTimeout(timer);
        }
        timer = null;
        client.clear();
        pendingChannelIds.clear();
        pendingFullRefresh = false;
        needsFullFetch = true;
      };
    },
    onRealtime(this: void, message: DashboardRealtimeMessage) {
      const channelChange = getOrgChannelChange(message);
      if (channelChange?.organizationId === scope?.organizationId) {
        invalidate(channelChange?.channelId);
      }
    },
    options,
    refreshAll() {
      invalidate();
    },
    async reload(this: void): Promise<boolean> {
      if (!scope || !connected) {
        return false;
      }
      pendingFullRefresh = true;
      await client.invalidateQueries({
        exact: true,
        queryKey: options.queryKey,
        refetchType: "none",
      });
      try {
        await client.fetchQuery(options);
        if (pendingFullRefresh || pendingChannelIds.size > 0) {
          await client.invalidateQueries({
            exact: true,
            queryKey: options.queryKey,
            refetchType: "none",
          });
          await client.fetchQuery(options);
        }
        return true;
      } catch {
        return false;
      }
    },
  };
}

export function getOrgChannelChange(message: DashboardRealtimeMessage) {
  if (
    message.type === "channel_changed" ||
    message.type === "channel_environment_changed"
  ) {
    return {
      channelId: message.payload.channel_id,
      organizationId: message.payload.organization_id,
    };
  }
  if (
    message.type === "instance_activity" &&
    (message.payload.activity_type === "channel_message" ||
      message.payload.activity_type === "channel_wake_claimed")
  ) {
    return {
      channelId: message.payload.detail.channel_id,
      organizationId: message.payload.organization_id,
    };
  }
  return null;
}

function mergeChannelRefresh(
  previous: ListChannelsAsUserResponse,
  refreshed: ListChannelsAsUserResponse,
  channelIds: readonly string[],
): ListChannelsAsUserResponse {
  const requested = new Set(channelIds);
  const replacements = new Map(
    refreshed.channels.map((channel) => [channel.id, channel]),
  );
  const existingIds = new Set(previous.channels.map((channel) => channel.id));
  return {
    channels: [
      ...previous.channels.flatMap((channel) => {
        if (!requested.has(channel.id)) {
          return [channel];
        }
        const replacement = replacements.get(channel.id);
        return replacement ? [replacement] : [];
      }),
      ...refreshed.channels.filter((channel) => !existingIds.has(channel.id)),
    ],
  };
}
